import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import {
  CollectiveAdvice,
  CoverageNeed,
  InsuranceAdvice,
  NeedName,
  PaymentConfiguration,
  PaymentRestriction,
  PaymentWarning,
  ProductCoverageName,
  ProductName,
  PurchaseRestriction,
} from "./CollectiveAdvice";

import { OneTimePaymentAdvisorChoice } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";

const getInsuranceAdvice = (
  need: CoverageNeed,
  productName: ProductName,
): InsuranceAdvice => {
  const insuranceAdvice = need.advice[productName];
  if (!isDefined(insuranceAdvice)) {
    throw Error(
      `getInsuranceAdvice() - insuranceAdvice is not defined for product ${productName}`,
    );
  }
  return insuranceAdvice;
};

export const isAnyCoverageAllowed = (advice: CollectiveAdvice): boolean =>
  Object.values(advice.needs)
    .flatMap((need) => Object.values(need.advice))
    .flatMap((insuranceAdvice) => Object.values(insuranceAdvice.payments))
    .map((payment) => payment.restrictions.purchaseRestriction)
    .includes(PurchaseRestriction.ALLOWED);

export const areAllCoveragesAllowed = (advice: CollectiveAdvice): boolean =>
  Object.values(advice.needs)
    .flatMap((need) => Object.values(need.advice))
    .flatMap((insuranceAdvice) => Object.values(insuranceAdvice.payments))
    .every(
      (payment) =>
        payment.restrictions.purchaseRestriction ===
        PurchaseRestriction.ALLOWED,
    );

export const getAllNeedCoverages = (
  need: CoverageNeed,
): ProductCoverageName[] =>
  Object.values(need.advice).flatMap(
    (insuranceAdvice) =>
      Object.keys(insuranceAdvice.payments) as ProductCoverageName[],
  );

export const getPayment = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): PaymentConfiguration => {
  const productName = getProductName(productCoverageName);
  const payment = getInsuranceAdvice(need, productName).payments[
    productCoverageName
  ];
  if (!isDefined(payment)) {
    throw Error(
      `getPayment() - payment is not defined for product ${productName} with coverage ${productCoverageName}`,
    );
  }
  return payment;
};

export const getCoveragePayment = (
  advice: CollectiveAdvice,
  productCoverageName: ProductCoverageName,
): PaymentConfiguration | undefined => {
  return advice.needs[getNeedName(productCoverageName)].advice[
    getProductName(productCoverageName)
  ]?.payments[productCoverageName];
};

export const getPaymentRestriction = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): PaymentRestriction =>
  getPayment(need, productCoverageName).restrictions.paymentRestriction;

export const isAllowedAmount = (
  amount: number,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  isAdvisor: boolean,
): boolean => {
  const authorizationLevel = isAdvisor
    ? UserAuthorizationLevel.PRODUCT_MANAGER
    : UserAuthorizationLevel.CUSTOMER;
  const { minPayouts, maxPayouts } = getPaymentRestriction(
    need,
    productCoverageName,
  );
  return (
    amount >= minPayouts[authorizationLevel] &&
    amount <= maxPayouts[authorizationLevel]
  );
};

export const isDisplayedAndChecked = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  isAllowed(need, productCoverageName) &&
  isRecommended(need, productCoverageName);

export const isDisplayedAndNotChecked = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  isAllowed(need, productCoverageName) &&
  !isRecommended(need, productCoverageName);

export const isNotDisplayed = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  isNotAllowed(need, productCoverageName) ||
  isNotOnline(need, productCoverageName);

export const isAllowed = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  getPayment(need, productCoverageName).restrictions.purchaseRestriction ===
  PurchaseRestriction.ALLOWED;

export const isNotAllowed = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  getPayment(need, productCoverageName).restrictions.purchaseRestriction ===
  PurchaseRestriction.NOT_ALLOWED;

const isNotOnline = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  getPayment(need, productCoverageName).restrictions.purchaseRestriction ===
  PurchaseRestriction.NOT_ONLINE;

export const getRecommended = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): number => getPayment(need, productCoverageName).advice.recommended;

export const isRecommended = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean => getRecommended(need, productCoverageName) > 0;

const getProductName = (
  productCoverageName: ProductCoverageName,
): ProductName => {
  switch (productCoverageName) {
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
    case ProductCoverageName.AKA_CRITICAL_ILLNESS_CO_INSURED:
      return INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_DEATH:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return INSURANCE_NAME_VALUE.AKA_GROUP_LIFE;
    case ProductCoverageName.CRITICAL_ILLNESS:
      return INSURANCE_NAME_VALUE.CRITICAL_ILLNESS;
    case ProductCoverageName.DEATH:
      return INSURANCE_NAME_VALUE.DEATH;
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return INSURANCE_NAME_VALUE.DISABILITY_FLEX;
  }
};

const getNeedName = (productCoverageName: ProductCoverageName): NeedName => {
  switch (productCoverageName) {
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
    case ProductCoverageName.AKA_CRITICAL_ILLNESS_CO_INSURED:
    case ProductCoverageName.CRITICAL_ILLNESS:
      return NeedName.CRITICAL_ILLNESS;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_DEATH:
    case ProductCoverageName.DEATH:
      return NeedName.LIFE;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return NeedName.DISABILITY;
  }
};

export const isCoverageSelected = (
  productCoverageName: ProductCoverageName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): boolean =>
  isNeedSelected(getNeedName(productCoverageName), formValues) &&
  isCoverageSelectedInCard(productCoverageName, formValues, isAdvisor);

export const isNeedSelected = (
  needName: NeedName,
  formValues: Partial<AkaAdvicePriceFormValues>,
): boolean => {
  switch (needName) {
    case NeedName.LIFE:
      return !!formValues[AkaAdviceCheckboxes.DEATH_CHECKBOX];
    case NeedName.DISABILITY:
      return !!formValues[AkaAdviceCheckboxes.DISABILITY_CHECKBOX];
    case NeedName.CRITICAL_ILLNESS:
      return !!formValues[AkaAdviceCheckboxes.CRITICAL_ILLNESS_CHECKBOX];
  }
};

export const isCoverageSelectedInCard = (
  productCoverageName: ProductCoverageName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): boolean => {
  switch (productCoverageName) {
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return formValues.akaCriticalIllnessCoverageAmountCheckbox ?? false;
    case ProductCoverageName.AKA_CRITICAL_ILLNESS_CO_INSURED:
      return (
        formValues.akaCriticalIllnessCoverageAmountCoInsuredCheckbox ?? false
      );
    case ProductCoverageName.CRITICAL_ILLNESS:
      return formValues.criticalIllnessInsuredSumCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
      return formValues.akaGroupLifeDeathCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_DEATH:
      return formValues.akaGroupLifeDeathCoInsuredCheckbox ?? false;
    case ProductCoverageName.DEATH:
      return formValues.deathInsuredSumCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
      return formValues.incapacityOfWorkCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return formValues.timeLimitedDisabilityInterestCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK:
      return formValues.incapacityOfWorkCoInsuredCheckbox ?? false;
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return formValues.timeLimitedDisabilityInterestCoInsuredCheckbox ?? false;
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return formValues.disabilityInsuredSumCheckbox ?? false;
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return isDisabilityCapitalCoverageSelected(formValues, isAdvisor);
  }
};

const isDisabilityCapitalCoverageSelected = (
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): boolean => {
  if (!isAdvisor) {
    return formValues.disabilityOneTimePaymentCheckbox ?? false;
  }
  switch (formValues.disabilityOneTimePaymentAdvisorChoice) {
    case OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT:
    case OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT:
      return true;
    case OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT:
    case undefined:
      return false;
  }
};

export const getSelectedCoverageValue = (
  productCoverageName: ProductCoverageName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): number => {
  if (!isCoverageSelected(productCoverageName, formValues, isAdvisor)) {
    return 0;
  }
  switch (productCoverageName) {
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return Number(formValues.akaCriticalIllnessCoverageAmountGLevel ?? 0);
    case ProductCoverageName.AKA_CRITICAL_ILLNESS_CO_INSURED:
      return Number(
        formValues.akaCriticalIllnessCoverageAmountCoInsuredGLevel ?? 0,
      );
    case ProductCoverageName.CRITICAL_ILLNESS:
      return formValues.criticalIllnessInsuredSumSlider ?? 0;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
      return Number(formValues.akaGroupLifeDeathGLevel ?? 0);
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_DEATH:
      return Number(formValues.akaGroupLifeDeathCoInsuredGLevel ?? 0);
    case ProductCoverageName.DEATH:
      return formValues.deathInsuredSumSlider ?? 0;
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
      return Number(formValues.incapacityOfWorkGLevel ?? 0);
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return Number(formValues.timeLimitedDisabilityInterestGLevel ?? 0);
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK:
      return Number(formValues.incapacityOfWorkCoInsuredGLevel ?? 0);
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return Number(
        formValues.timeLimitedDisabilityInterestCoInsuredGLevel ?? 0,
      );
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return formValues.disabilityInsuredSumSlider ?? 0;
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return getDisabilityCapitalSelectedCoverage(formValues);
  }
};

const getDisabilityCapitalSelectedCoverage = (
  formValues: Partial<AkaAdvicePriceFormValues>,
): number => {
  if (
    OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT ===
    formValues.disabilityOneTimePaymentAdvisorChoice
  ) {
    return formValues.disabilityOneTimePaymentAdvisorValue ?? 0;
  }
  return formValues.disabilityOneTimePaymentValue ?? 0;
};

export const isCloseToMaxAge = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  getPayment(need, productCoverageName).warnings.includes(
    PaymentWarning.CLOSE_TO_MAX_AGE,
  );
